"use client";
import { GoogleTagManager as _GoogleTagManager } from "@next/third-parties/google";
import { MakePropertyOptional } from "@itchannelplus/utils/ts-utils";
import { useEffect } from "react";

type GTMParams = React.ComponentProps<typeof _GoogleTagManager>;

export const DefaultConsent = () => {
  useEffect(() => {
    gtag("consent", "default", {
      functionality_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 5000,
    });
    gtag("set", "url_passthrough", true);
    gtag("set", "ads_data_redaction", true);
  }, []);
  return <></>;
};

export const GoogleTagManager = (
  props: MakePropertyOptional<GTMParams, "gtmId"> & { gtmHost?: string },
) => {
  const gtmId = props.gtmId || process.env.NEXT_PUBLIC_GTM_ID;
  const gtmHost =
    props.gtmHost ||
    process.env.NEXT_PUBLIC_GTM_HOST ||
    "www.googletagmanager.com";
  const gtmScriptUrl = `https://${gtmHost}/gtm.js`;
  return (
    <>
      {!!gtmId && (
        <>
          <DefaultConsent />
          <_GoogleTagManager
            {...props}
            gtmId={gtmId}
            gtmScriptUrl={gtmScriptUrl}
          />
        </>
      )}
    </>
  );
};

export const GoogleTagManagerNoScript = (
  props: MakePropertyOptional<GTMParams, "gtmId"> & { gtmHost?: string },
) => {
  const gtmHost =
    props.gtmHost ||
    process.env.NEXT_PUBLIC_GTM_HOST ||
    props.gtmScriptUrl?.substring(0, props.gtmScriptUrl.lastIndexOf("/")) ||
    "www.googletagmanager.com";
  const url = `https://${gtmHost}/ns.html`;
  const gtmId = props.gtmId || process.env.NEXT_PUBLIC_GTM_ID;
  return (
    <>
      {!!gtmId && (
        <noscript>
          <iframe
            src={`${url}?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          ></iframe>
        </noscript>
      )}
    </>
  );
};

export const GoogleTagManagerEvent = (props: {
  name: string;
  data?: any;
  ecommerceData?: any;
  gtag?: any;
}) => {
  useEffect(() => {
    if (props.ecommerceData) {
      pushEcommerceDataLayerEvent(props.name, {
        ...props.ecommerceData,
      });
    }
    if (props.data) {
      pushDataLayerEvent(props.name, {
        ...props.data,
      });
    }
    if (props.gtag) {
      gtag("event", props.name, props.gtag);
    }
  }, [props.gtag, props.ecommerceData, props.data, props.name]);
  return <></>;
};

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
  gtag: (...args: any[]) => void;
};

declare const window: WindowWithDataLayer;

/**
 * Push data to the data layer
 */

export const pageView = (url: string, title?: string) => {
  pushDataLayerEvent("virtual_page_view", {
    virtual_page_location: url,
    ...(title ? { virtual_page_title: title } : {}),
  });
};

/**
 * push data to the data layer
 * @param data
 */
export const pushDataLayer = (data: Record<string, any>) => {
  if (typeof window != "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }
};

export const gtag = (...data: (Record<string, any> | string | boolean)[]) => {
  if (typeof window != "undefined") {
    if (typeof window.gtag == "undefined") {
      window.gtag = function () {
        window.dataLayer = window.dataLayer || [];
        // @ts-ignore
        window.dataLayer.push(arguments);
      };
    }
    window.gtag(...data);
  }
};

export const gtagEvent = (name: string, data: Record<string, any>) => {
  gtag("event", name, data);
};

export function pushDataLayerEvent(eventName: string, data: any) {
  const newData = {
    event: eventName,
    ...data,
  };
  if (process.env.NEXT_PUBLIC_GTM_DEBUG) {
    console.log("Detected event:", eventName);
    console.table(data);
  }
  pushDataLayer(newData);
}

export function pushEcommerceDataLayerEvent(
  eventName: string,
  ecommerceData: any,
) {
  // Clear the previous ecommerce object
  // Prevents multiple ecommerce events on a page from affecting each other
  pushDataLayer({ ecommerce: null });

  // Push the new ecommerce object
  pushDataLayerEvent(eventName, {
    ecommerce: ecommerceData,
  });
  if (process.env.NEXT_PUBLIC_GTM_DEBUG) {
    console.log("Items:", ecommerceData?.items?.length ?? 0);
    console.table(ecommerceData?.items);
  }
}
